import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  ButtonNext, ButtonBack, Slider, Slide, WithStore, Dot,
} from 'pure-react-carousel';
import cx from 'classnames';
import Button from '../Button';
import Icon from '../Icon';
import TranslationProvider from '../../providers/TranslationProvider';
import Caret from '../../svg/icons/caret.svg';
import Close from '../../svg/icons/ic_close_20x20.svg';

import 'pure-react-carousel/dist/react-carousel.es.css';
import './ModalGallery.scss';

// ModalGallery needs to be nested in a Carousel Provider Component
const ModalGallery = ({
  slides, toggleModal, currentSlide, isMobile, thumbnails,
}) => {
  useEffect(() => {
    const slider = document.getElementById('ModalGallery__slider');

    if (slider) {
      slider.focus();
    }
  }, []);

  const renderDots = (dotSlides, dotCurrentSlide) => (
    <div className="ModalGallery__Dot-Group">
      <div className="dot-group-images">
        {dotSlides.map((slide, index) => (
          <Dot slide={index} key={`dot-${slide.id}`}>
            <div
              className={cx('ImageCarousel__dot-image', {
                'ImageCarousel__dot-image--selected': dotCurrentSlide === index,
              })}
              style={{ backgroundImage: `url(${slide.url})` }}
            />
          </Dot>
        )) }
      </div>
    </div>
  );

  const renderHeader = (slideNumber, totalSlides, toggleGalleryModal) => (
    <div className="ModalGallery__Header">
      <TranslationProvider
        className="ModalGallery__Header__heading"
        translationKey="modal_gallery_count"
        translationValues={{ slideNumber: String(slideNumber), totalSlides: String(totalSlides) }}
      >
        <p>{`${slideNumber} of ${totalSlides}`}</p>
      </TranslationProvider>
      <div className="ModalGallery__Header__close">
        <Button
          variant="black-small"
          onClick={toggleGalleryModal}
        >
          <TranslationProvider
            className="modal-close-copy"
            translationKey="close"
          >
            Close
          </TranslationProvider>
          <Icon component={Close} name="close" />
        </Button>
      </div>
    </div>
  );

  if (!slides.length) return null;

  const slideNumber = currentSlide + 1;
  const totalSlides = slides.length;
  const styleObj = isMobile ? { maxWidth: '100vw' } : { maxHeight: '80vh' };

  return (
    <div data-testId="Modal-Gallery" className="ModalGallery">
      {renderHeader(slideNumber, totalSlides, toggleModal)}
      <div className="ModalGallery__Slider-Wrapper">
        {!isMobile && (
          <ButtonBack>
            <Icon component={Caret} name="Caret" />
          </ButtonBack>
        )}
        <Slider id="ModalGallery__slider" tabIndex={-1}>
          {slides.map((slide, index) => (
            <Slide tabIndex={-1} index={index} key={slide.url}>
              <div className="modal-image-wrapper">
                <img
                  alt={`slide-${index}`}
                  className="img-responsive"
                  src={slide.url}
                  style={styleObj}
                />
              </div>
            </Slide>
          ))}
        </Slider>
        {!isMobile && (
          <ButtonNext>
            <Icon component={Caret} name="Caret" />
          </ButtonNext>
        ) }
      </div>
      {!isMobile && (
        renderDots(thumbnails, currentSlide)
      )}
    </div>
  );
};

ModalGallery.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
  })),
  thumbnails: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
  })),
  toggleModal: PropTypes.func.isRequired,
  currentSlide: PropTypes.number,
  isMobile: PropTypes.bool,
};

ModalGallery.defaultProps = {
  slides: [],
  thumbnails: [],
  currentSlide: null,
  isMobile: false,
};

ModalGallery.displayName = 'ModalGallery';
export default WithStore(ModalGallery, (state) => ({
  currentSlide: state.currentSlide,
}));

export { ModalGallery as ModalGalleryComponent };
